import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  IconButton,
  MenuItem,
  Paper,
  Snackbar,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import RefreshIcon from "@mui/icons-material/Refresh";
import { API_URL } from "./config";
import {
  getCampusFromToken,
  getAuthHeaders,
} from "./commonfunctions/useAuthHelpers";

const AIProviderConfigsPage = () => {
  const THEME_ORANGE = "#F07828";
  const THEME_ORANGE_2 = "#ed7f35ff";
  const BG = "#F2F2F2";
  const BORDER = "rgba(25,118,210,0.20)";
  const BORDER_HOVER = "rgba(25,118,210,0.45)";
  const BORDER_FOCUS = "rgba(240,120,40,0.80)";
  const ROW_ALT = "rgba(25,118,210,0.06)";
  const ROW_HOVER = "rgba(25,118,210,0.10)";

  const pageWrapSx = { p: 2, backgroundColor: BG };
  const rootPaperSx = {
    mx: "auto",
    borderRadius: 2,
    overflow: "hidden",
    border: "1px solid rgba(25,118,210,0.15)",
  };
  const headerSx = {
    px: 2,
    py: 1.5,
    background: `linear-gradient(90deg, ${THEME_ORANGE} 0%, ${THEME_ORANGE_2} 100%)`,
    color: "white",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 2,
    flexWrap: "wrap",
  };
  const bodySx = { p: 2, backgroundColor: BG };
  const surfaceSx = {
    borderRadius: 2,
    backgroundColor: "white",
    border: `1px solid ${BORDER}`,
  };
  const inputSx = {
    "& .MuiOutlinedInput-notchedOutline": { borderColor: BORDER },
    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: BORDER_HOVER },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: BORDER_FOCUS },
  };
  const primaryBtnSx = {
    borderRadius: 2,
    textTransform: "none",
    fontWeight: 900,
    backgroundColor: THEME_ORANGE,
    "&:hover": { backgroundColor: THEME_ORANGE_2 },
  };
  const secondaryBtnSx = {
    borderRadius: 2,
    textTransform: "none",
    fontWeight: 800,
  };
  const iconBtnSx = {
    borderRadius: 2,
    color: "rgba(25,118,210,0.95)",
    "&:hover": { backgroundColor: "rgba(240,120,40,0.10)", color: THEME_ORANGE },
  };
  const checkboxSx = {
    color: "rgba(25,118,210,0.75)",
    "&.Mui-checked": { color: THEME_ORANGE },
  };

  const serialNumberSx = {
    minWidth: 34,
    height: 34,
    px: 1,
    borderRadius: "18px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: 900,
    color: "rgba(25,118,210,0.95)",
    backgroundColor: "rgba(25,118,210,0.10)",
    border: "1px solid rgba(25,118,210,0.22)",
  };
  const tableWrapSx = {
    ...surfaceSx,
    mt: 1,
    borderRadius: 2,
    overflow: "hidden",
  };
  const theadRowSx = {
    backgroundColor: "rgba(25,118,210,0.95)",
    "& th": {
      color: "#fff",
      fontWeight: 900,
      borderBottom: "1px solid rgba(255,255,255,0.18)",
      whiteSpace: "nowrap",
    },
  };
  const zebraRowSx = (idx) => ({
    backgroundColor: idx % 2 === 0 ? ROW_ALT : "#FFFFFF",
    "&:hover": { backgroundColor: ROW_HOVER },
    "& td": { borderBottom: "1px solid rgba(25,118,210,0.10)" },
  });

  const providerOptions = useMemo(
    () => [
      { value: "GEMINI", label: "Gemini" },
      { value: "OPENAI", label: "OpenAI" },
      { value: "ANTHROPIC", label: "Anthropic" },
      { value: "AZURE_OPENAI", label: "Azure OpenAI" },
      { value: "OLLAMA_LOCAL", label: "Ollama (DB configured)" },
      { value: "UNIFLOO_OLLAMA", label: "UniFLOO Ollama Server" },
      { value: "DEEPSEEK", label: "DeepSeek" },
      { value: "CUSTOM", label: "Custom" },
    ],
    []
  );

  const getDefaultApiBaseUrl = (provider) => {
    switch (String(provider || "").toUpperCase()) {
      case "OPENAI":
        return "https://api.openai.com/v1";
      case "ANTHROPIC":
        return "https://api.anthropic.com";
      case "GEMINI":
      case "AZURE_OPENAI":
      case "OLLAMA_LOCAL":
      case "CUSTOM":
      default:
        return "";
    }
  };

  const [tab, setTab] = useState(0);
  const [campusCode, setCampusCode] = useState("");
  const [subCampusCodes, setSubCampusCodes] = useState([]);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  // AI Configs tab state
  const [rows, setRows] = useState([]);
  const [editRowId, setEditRowId] = useState(null);
  const [newRow, setNewRow] = useState({
    SubCampusCode: "",
    Provider: "GEMINI",
    DisplayName: "",
    ApiBaseUrl: getDefaultApiBaseUrl("GEMINI"),
    ApiKey: "",
    ModelName: "",
    IsActive: 1,
    IsDefault: 0,
  });

  // Feature assignment tab state
  const [assignmentSubCampusCode, setAssignmentSubCampusCode] = useState("");
  const [featureRows, setFeatureRows] = useState([]);
  const [assignmentConfigs, setAssignmentConfigs] = useState([]);
  const [featureLoading, setFeatureLoading] = useState(false);
  const [savingFeatureKey, setSavingFeatureKey] = useState("");
  const [selectedFeatureKeys, setSelectedFeatureKeys] = useState([]);
  const [bulkAssignmentValue, setBulkAssignmentValue] = useState("");
  const [bulkSaving, setBulkSaving] = useState(false);

  useEffect(() => {
    const cc = getCampusFromToken?.() || "";
    setCampusCode(cc);
  }, []);

  useEffect(() => {
    if (!campusCode) return;

    axios
      .get(`${API_URL}/api/ValidateCampus/${campusCode}`, {
        headers: getAuthHeaders?.(),
      })
      .then((res) => setSubCampusCodes(res.data?.subcampuses || []))
      .catch((err) => {
        console.error(err);
        setError("Failed to fetch subcampuses");
      });
  }, [campusCode]);

  const fetchRows = async () => {
    const res = await axios.get(`${API_URL}/api/ai-provider-configs/list`, {
      headers: getAuthHeaders?.(),
    });
    setRows(res.data || []);
  };

  useEffect(() => {
    if (!campusCode) return;
    fetchRows().catch((err) => {
      setError(err?.response?.data?.error || "Failed to load AI provider configs");
    });
  }, [campusCode]);

  const fetchFeatureAssignments = async () => {
    if (!campusCode) return;

    try {
      setFeatureLoading(true);
      const params = {};
      if (assignmentSubCampusCode) {
        params.subCampusCode = assignmentSubCampusCode;
      }

      const res = await axios.get(
        `${API_URL}/api/ai-provider-configs/feature-assignments`,
        {
          headers: getAuthHeaders?.(),
          params,
        }
      );

      setFeatureRows(res.data?.features || []);
      setAssignmentConfigs(res.data?.configs || []);
    } catch (err) {
      setError(err?.response?.data?.error || "Failed to load AI feature assignments");
    } finally {
      setFeatureLoading(false);
    }
  };

  useEffect(() => {
    if (!campusCode || tab !== 1) return;
    fetchFeatureAssignments();
  }, [campusCode, tab, assignmentSubCampusCode]);

  const handleNewChange = (e) => {
    const { name, value, type, checked } = e.target;

    setNewRow((prev) => {
      if (type === "checkbox") {
        return { ...prev, [name]: checked ? 1 : 0 };
      }

      if (name === "Provider") {
        return {
          ...prev,
          Provider: value,
          ApiBaseUrl: getDefaultApiBaseUrl(value),
        };
      }

      return { ...prev, [name]: value };
    });
  };

  const handleEditChange = (id, e) => {
    const { name, value, type, checked } = e.target;

    setRows((prev) =>
      prev.map((r) => {
        if (r.AiConfigId !== id) return r;

        if (type === "checkbox") {
          return { ...r, [name]: checked ? 1 : 0 };
        }

        if (name === "Provider") {
          const oldDefault = getDefaultApiBaseUrl(r.Provider);
          const newDefault = getDefaultApiBaseUrl(value);
          const currentUrl = r.ApiBaseUrl || "";
          const shouldReplaceUrl = !currentUrl || currentUrl === oldDefault;

          return {
            ...r,
            Provider: value,
            ApiBaseUrl: shouldReplaceUrl ? newDefault : currentUrl,
          };
        }

        return { ...r, [name]: value };
      })
    );
  };

  const handleAdd = async () => {
    try {
      await axios.post(`${API_URL}/api/ai-provider-configs/list`, newRow, {
        headers: getAuthHeaders?.(),
      });

      setSuccessMessage("AI configuration added");
      setNewRow({
        SubCampusCode: "",
        Provider: "GEMINI",
        DisplayName: "",
        ApiBaseUrl: getDefaultApiBaseUrl("GEMINI"),
        ApiKey: "",
        ModelName: "",
        IsActive: 1,
        IsDefault: 0,
      });
      await fetchRows();
    } catch (err) {
      setError(err?.response?.data?.error || "Failed to add AI provider config");
    }
  };

  const handleUpdate = async (id) => {
    try {
      const row = rows.find((r) => r.AiConfigId === id);
      await axios.put(`${API_URL}/api/ai-provider-configs/list/${id}`, row, {
        headers: getAuthHeaders?.(),
      });

      setSuccessMessage("AI configuration updated");
      setEditRowId(null);
      await fetchRows();
      if (tab === 1) await fetchFeatureAssignments();
    } catch (err) {
      setError(err?.response?.data?.error || "Failed to update AI provider config");
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API_URL}/api/ai-provider-configs/list/${id}`, {
        headers: getAuthHeaders?.(),
      });

      setSuccessMessage("AI configuration deleted");
      await fetchRows();
    } catch (err) {
      setError(err?.response?.data?.error || "Failed to delete AI provider config");
    }
  };

  const handleFeatureConfigChange = (featureKey, value) => {
    setFeatureRows((prev) =>
      prev.map((row) => {
        if (row.FeatureKey !== featureKey) return row;

        if (value === "__LOCAL_OLLAMA__") {
          return {
            ...row,
            ProviderMode: "LOCAL_OLLAMA",
            AiConfigId: null,
          };
        }

        if (value === "__NONE__") {
          return {
            ...row,
            ProviderMode: "NONE",
            AiConfigId: null,
          };
        }

        if (value === "") {
          return {
            ...row,
            ProviderMode: null,
            AiConfigId: null,
          };
        }

        return {
          ...row,
          ProviderMode: "AI_CONFIG",
          AiConfigId: Number(value),
        };
      })
    );
  };

  const toggleFeatureSelection = (featureKey) => {
    setSelectedFeatureKeys((prev) =>
      prev.includes(featureKey)
        ? prev.filter((key) => key !== featureKey)
        : [...prev, featureKey]
    );
  };

  const toggleSelectAllFeatures = () => {
    const allKeys = featureRows.map((row) => row.FeatureKey);
    const allSelected = allKeys.length > 0 && allKeys.every((key) => selectedFeatureKeys.includes(key));
    setSelectedFeatureKeys(allSelected ? [] : allKeys);
  };

  const bulkAssignFeatures = async () => {
    if (!selectedFeatureKeys.length) {
      setError("Select at least one AI feature row");
      return;
    }

    const ProviderMode =
      bulkAssignmentValue === "__NONE__"
        ? "NONE"
        : bulkAssignmentValue === "__LOCAL_OLLAMA__"
        ? "LOCAL_OLLAMA"
        : bulkAssignmentValue
        ? "AI_CONFIG"
        : null;
    const AiConfigId = ProviderMode === "AI_CONFIG" ? Number(bulkAssignmentValue) : null;

    try {
      setBulkSaving(true);
      await Promise.all(
        selectedFeatureKeys.map((featureKey) =>
          axios.put(
            `${API_URL}/api/ai-provider-configs/feature-assignments/${encodeURIComponent(featureKey)}`,
            {
              SubCampusCode: assignmentSubCampusCode || null,
              ProviderMode,
              AiConfigId,
            },
            { headers: getAuthHeaders?.() }
          )
        )
      );
      setSuccessMessage(`Updated ${selectedFeatureKeys.length} AI feature assignment(s)`);
      setSelectedFeatureKeys([]);
      await fetchFeatureAssignments();
    } catch (err) {
      setError(err?.response?.data?.error || "Failed to bulk assign AI features");
    } finally {
      setBulkSaving(false);
    }
  };

  const saveFeatureAssignment = async (feature) => {
    try {
      setSavingFeatureKey(feature.FeatureKey);

      await axios.put(
        `${API_URL}/api/ai-provider-configs/feature-assignments/${encodeURIComponent(
          feature.FeatureKey
        )}`,
        {
          SubCampusCode: assignmentSubCampusCode || null,
          ProviderMode: feature.ProviderMode || null,
          AiConfigId:
            feature.ProviderMode === "AI_CONFIG"
              ? feature.AiConfigId || null
              : null,
        },
        { headers: getAuthHeaders?.() }
      );

      setSuccessMessage(
        feature.ProviderMode
          ? `AI service assigned to ${feature.FeatureName || feature.FeatureKey}`
          : `Assignment cleared for ${feature.FeatureName || feature.FeatureKey}`
      );
      await fetchFeatureAssignments();
    } catch (err) {
      setError(err?.response?.data?.error || "Failed to save AI feature assignment");
    } finally {
      setSavingFeatureKey("");
    }
  };

  const renderConfigsTab = () => (
    <>
      <Paper elevation={0} sx={{ ...surfaceSx, p: 2, mb: 2 }}>
        <Typography sx={{ fontWeight: 800, mb: 1.5, color: "rgba(25,118,210,0.95)" }}>
          Add New Configuration
        </Typography>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(2, minmax(220px, 1fr))",
              lg: "repeat(4, minmax(220px, 1fr))",
            },
            gap: 1.5,
          }}
        >
          <TextField
            select
            name="SubCampusCode"
            label="SubCampusCode"
            value={newRow.SubCampusCode}
            onChange={handleNewChange}
            size="small"
            fullWidth
            sx={inputSx}
          >
            <MenuItem value="">Campus-wide</MenuItem>
            {subCampusCodes.map((sc) => (
              <MenuItem key={sc.SubCampusCode} value={sc.SubCampusCode}>
                {sc.SubCampusCode}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            name="Provider"
            label="Provider"
            value={newRow.Provider}
            onChange={handleNewChange}
            size="small"
            fullWidth
            sx={inputSx}
          >
            {providerOptions.map((p) => (
              <MenuItem key={p.value} value={p.value}>
                {p.label}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            name="DisplayName"
            label="Display Name"
            value={newRow.DisplayName}
            onChange={handleNewChange}
            size="small"
            fullWidth
            sx={inputSx}
          />

          <TextField
            name="ModelName"
            label="Model Name"
            value={newRow.ModelName}
            onChange={handleNewChange}
            size="small"
            fullWidth
            sx={inputSx}
          />

          <TextField
            name="ApiBaseUrl"
            label="API Base URL"
            value={newRow.ApiBaseUrl}
            onChange={handleNewChange}
            size="small"
            fullWidth
            sx={inputSx}
            placeholder={
              newRow.Provider === "AZURE_OPENAI"
                ? "https://your-resource.openai.azure.com"
                : newRow.Provider === "OLLAMA_LOCAL"
                ? "http://127.0.0.1:11434"
                : ""
            }
          />

          <TextField
            name="ApiKey"
            label="API Key"
            value={newRow.ApiKey}
            onChange={handleNewChange}
            size="small"
            fullWidth
            type="password"
            disabled={newRow.Provider === "OLLAMA_LOCAL"}
            helperText={
              newRow.Provider === "OLLAMA_LOCAL"
                ? "No API key required for local Ollama"
                : "Stored encrypted by the backend"
            }
            sx={inputSx}
          />

          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Checkbox
              name="IsActive"
              checked={!!newRow.IsActive}
              onChange={handleNewChange}
              sx={checkboxSx}
            />
            <Typography>Active</Typography>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Checkbox
              name="IsDefault"
              checked={!!newRow.IsDefault}
              onChange={handleNewChange}
              sx={checkboxSx}
            />
            <Typography>Default</Typography>
          </Box>
        </Box>

        <Box sx={{ mt: 2 }}>
          <Button
            variant="contained"
            sx={primaryBtnSx}
            startIcon={<AddIcon />}
            onClick={handleAdd}
          >
            Add Config
          </Button>
        </Box>
      </Paper>

      <Paper elevation={0} sx={tableWrapSx}>
        <Box sx={{ overflowX: "auto", overflowY: "hidden" }}>
          <TableContainer component="div">
            <Table size="small">
              <TableHead>
                <TableRow sx={theadRowSx}>
                  <TableCell>#</TableCell>
                  <TableCell>Campus</TableCell>
                  <TableCell>SubCampus</TableCell>
                  <TableCell>Provider</TableCell>
                  <TableCell>Display Name</TableCell>
                  <TableCell>Model</TableCell>
                  <TableCell>API Base URL</TableCell>
                  <TableCell>API Key</TableCell>
                  <TableCell>Active</TableCell>
                  <TableCell>Default</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {rows.map((row, idx) => {
                  const isEdit = editRowId === row.AiConfigId;

                  if (isEdit) {
                    return (
                      <TableRow key={row.AiConfigId} sx={zebraRowSx(idx)}>
                        <TableCell><Box component="span" sx={serialNumberSx}>{idx + 1}</Box></TableCell>
                        <TableCell>{row.CampusCode}</TableCell>
                        <TableCell>
                          <TextField
                            select
                            name="SubCampusCode"
                            value={row.SubCampusCode || ""}
                            onChange={(e) => handleEditChange(row.AiConfigId, e)}
                            size="small"
                            fullWidth
                            sx={inputSx}
                          >
                            <MenuItem value="">Campus-wide</MenuItem>
                            {subCampusCodes.map((sc) => (
                              <MenuItem key={sc.SubCampusCode} value={sc.SubCampusCode}>
                                {sc.SubCampusCode}
                              </MenuItem>
                            ))}
                          </TextField>
                        </TableCell>
                        <TableCell>
                          <TextField
                            select
                            name="Provider"
                            value={row.Provider}
                            onChange={(e) => handleEditChange(row.AiConfigId, e)}
                            size="small"
                            fullWidth
                            sx={inputSx}
                          >
                            {providerOptions.map((p) => (
                              <MenuItem key={p.value} value={p.value}>
                                {p.label}
                              </MenuItem>
                            ))}
                          </TextField>
                        </TableCell>
                        <TableCell>
                          <TextField
                            name="DisplayName"
                            value={row.DisplayName || ""}
                            onChange={(e) => handleEditChange(row.AiConfigId, e)}
                            size="small"
                            fullWidth
                            sx={inputSx}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            name="ModelName"
                            value={row.ModelName || ""}
                            onChange={(e) => handleEditChange(row.AiConfigId, e)}
                            size="small"
                            fullWidth
                            sx={inputSx}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            name="ApiBaseUrl"
                            value={row.ApiBaseUrl || ""}
                            onChange={(e) => handleEditChange(row.AiConfigId, e)}
                            size="small"
                            fullWidth
                            sx={inputSx}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            name="ApiKey"
                            value={row.ApiKey || ""}
                            onChange={(e) => handleEditChange(row.AiConfigId, e)}
                            size="small"
                            fullWidth
                            type="password"
                            disabled={row.Provider === "OLLAMA_LOCAL"}
                            placeholder="Leave blank to keep existing"
                            sx={inputSx}
                          />
                        </TableCell>
                        <TableCell>
                          <Checkbox
                            name="IsActive"
                            checked={!!row.IsActive}
                            onChange={(e) => handleEditChange(row.AiConfigId, e)}
                            sx={checkboxSx}
                          />
                        </TableCell>
                        <TableCell>
                          <Checkbox
                            name="IsDefault"
                            checked={!!row.IsDefault}
                            onChange={(e) => handleEditChange(row.AiConfigId, e)}
                            sx={checkboxSx}
                          />
                        </TableCell>
                        <TableCell>
                          <IconButton
                            sx={iconBtnSx}
                            onClick={() => handleUpdate(row.AiConfigId)}
                            title="Save"
                          >
                            <SaveIcon />
                          </IconButton>
                          <IconButton
                            sx={iconBtnSx}
                            onClick={() => setEditRowId(null)}
                            title="Cancel"
                          >
                            <CancelIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  }

                  return (
                    <TableRow key={row.AiConfigId} sx={zebraRowSx(idx)}>
                      <TableCell><Box component="span" sx={serialNumberSx}>{idx + 1}</Box></TableCell>
                      <TableCell>{row.CampusCode}</TableCell>
                      <TableCell>{row.SubCampusCode || "Campus-wide"}</TableCell>
                      <TableCell>{row.Provider}</TableCell>
                      <TableCell>{row.DisplayName}</TableCell>
                      <TableCell>{row.ModelName}</TableCell>
                      <TableCell>{row.ApiBaseUrl || ""}</TableCell>
                      <TableCell>{row.ApiKeyMasked || "********"}</TableCell>
                      <TableCell>
                        <Checkbox checked={!!row.IsActive} readOnly sx={checkboxSx} />
                      </TableCell>
                      <TableCell>
                        <Checkbox checked={!!row.IsDefault} readOnly sx={checkboxSx} />
                      </TableCell>
                      <TableCell>
                        <IconButton
                          sx={iconBtnSx}
                          onClick={() => setEditRowId(row.AiConfigId)}
                          title="Edit"
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          sx={iconBtnSx}
                          onClick={() => handleDelete(row.AiConfigId)}
                          title="Delete"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Paper>
    </>
  );

  const renderFeatureAssignmentsTab = () => (
    <>
      <Paper elevation={0} sx={{ ...surfaceSx, p: 2, mb: 2 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: { xs: "stretch", md: "center" },
            justifyContent: "space-between",
            flexDirection: { xs: "column", md: "row" },
            gap: 1.5,
          }}
        >
          <Box>
            <Typography sx={{ fontWeight: 900, color: "rgba(25,118,210,0.95)" }}>
              Assign AI Config by Feature
            </Typography>
            <Typography sx={{ fontSize: "0.85rem", opacity: 0.75, mt: 0.25 }}>
              Campus assignments are defaults. A subcampus assignment overrides the campus assignment.
            </Typography>
          </Box>

          <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
            <TextField
              select
              size="small"
              label="Assignment Scope"
              value={assignmentSubCampusCode}
              onChange={(e) => setAssignmentSubCampusCode(e.target.value)}
              sx={{ ...inputSx, minWidth: 220 }}
            >
              <MenuItem value="">Campus-wide</MenuItem>
              {subCampusCodes.map((sc) => (
                <MenuItem key={sc.SubCampusCode} value={sc.SubCampusCode}>
                  {sc.SubCampusCode}
                </MenuItem>
              ))}
            </TextField>

            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              sx={secondaryBtnSx}
              onClick={fetchFeatureAssignments}
              disabled={featureLoading}
            >
              Refresh
            </Button>
          </Box>
        </Box>
      </Paper>

      <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
        Assign a saved AI configuration to a feature when it needs a specific service. If no service is
        assigned, the feature uses the active <strong>Default</strong> AI configuration.
        <strong> None</strong> disables AI for that feature. <strong>Local Ollama (.env)</strong> uses OLLAMA_BASE_URL and OLLAMA_MODEL_NAME only when explicitly selected.
      </Alert>

      <Paper elevation={0} sx={{ ...surfaceSx, p: 2, mb: 2 }}>
        <Box sx={{ display: "flex", gap: 1.5, alignItems: "center", flexWrap: "wrap" }}>
          <Typography sx={{ fontWeight: 800 }}>
            Bulk assign {selectedFeatureKeys.length ? `(${selectedFeatureKeys.length} selected)` : ""}
          </Typography>
          <TextField
            select
            size="small"
            value={bulkAssignmentValue}
            onChange={(e) => setBulkAssignmentValue(e.target.value)}
            sx={{ ...inputSx, minWidth: 300 }}
          >
            <MenuItem value=""><em>Use default AI service</em></MenuItem>
            <MenuItem value="__NONE__">None — do not use AI</MenuItem>
            <MenuItem value="__LOCAL_OLLAMA__">Local Ollama (.env)</MenuItem>
            {assignmentConfigs.map((cfg) => (
              <MenuItem key={cfg.AiConfigId} value={String(cfg.AiConfigId)}>
                {cfg.DisplayName || cfg.Provider} — {cfg.ModelName || "No model"}
                {cfg.SubCampusCode ? ` (${cfg.SubCampusCode})` : " (Campus)"}
              </MenuItem>
            ))}
          </TextField>
          <Button
            variant="contained"
            startIcon={bulkSaving ? <CircularProgress size={14} color="inherit" /> : <SaveIcon />}
            sx={primaryBtnSx}
            disabled={bulkSaving || !selectedFeatureKeys.length}
            onClick={bulkAssignFeatures}
          >
            Assign Selected
          </Button>
        </Box>
      </Paper>

      <Paper elevation={0} sx={tableWrapSx}>
        {featureLoading ? (
          <Box sx={{ p: 4, display: "flex", justifyContent: "center" }}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <Box sx={{ overflowX: "auto" }}>
            <TableContainer component="div">
              <Table size="small">
                <TableHead>
                  <TableRow sx={theadRowSx}>
                    <TableCell padding="checkbox">
                      <Checkbox
                        sx={{
                          color: "rgba(255,255,255,0.95)",
                          "&.Mui-checked": { color: "#fff" },
                          "&.MuiCheckbox-indeterminate": { color: "#fff" },
                        }}
                        checked={featureRows.length > 0 && featureRows.every((row) => selectedFeatureKeys.includes(row.FeatureKey))}
                        indeterminate={selectedFeatureKeys.length > 0 && !featureRows.every((row) => selectedFeatureKeys.includes(row.FeatureKey))}
                        onChange={toggleSelectAllFeatures}
                      />
                    </TableCell>
                    <TableCell>#</TableCell>
                    <TableCell>Feature</TableCell>
                    {/*<TableCell>Feature Key</TableCell>*/}
                    <TableCell>Routing Mode</TableCell>
                    <TableCell>AI Config</TableCell>
                    <TableCell>Effective Assignment</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {featureRows.map((feature, idx) => {
                    const inherited =
                      !!assignmentSubCampusCode &&
                      feature.AssignmentScope === "CAMPUS" &&
                      !feature.IsExactScopeAssignment;

                    const selectionValue =
                      feature.ProviderMode === "LOCAL_OLLAMA"
                        ? "__LOCAL_OLLAMA__"
                        : feature.ProviderMode === "NONE"
                        ? "__NONE__"
                        : feature.ProviderMode === "AI_CONFIG" && feature.AiConfigId
                        ? String(feature.AiConfigId)
                        : "";

                    const routingLabel = feature.ProviderMode
                      ? feature.ProviderMode
                      : "DEFAULT AI";

                    return (
                      <TableRow key={feature.FeatureKey} sx={zebraRowSx(idx)}>
                        <TableCell padding="checkbox">
                          <Checkbox
                            sx={{
                              color: "rgba(25,118,210,0.55)",
                              "&.Mui-checked": { color: "rgba(25,118,210,0.95)" },
                            }}
                            checked={selectedFeatureKeys.includes(feature.FeatureKey)}
                            onChange={() => toggleFeatureSelection(feature.FeatureKey)}
                          />
                        </TableCell>
                        <TableCell><Box component="span" sx={serialNumberSx}>{idx + 1}</Box></TableCell>
                        <TableCell sx={{ minWidth: 220 }}>
                          <Typography sx={{ fontWeight: 800 }}>
                            {feature.FeatureName || feature.FeatureKey}
                          </Typography>
                          {!!feature.Description && (
                            <Typography sx={{ fontSize: "0.78rem", opacity: 0.7, mt: 0.25 }}>
                              {feature.Description}
                            </Typography>
                          )}
                        </TableCell>
                        {/*
                        <TableCell>
                          <Typography sx={{ fontFamily: "monospace", fontSize: "0.82rem" }}>
                            {feature.FeatureKey}
                          </Typography>
                        </TableCell>
                        */}
                        <TableCell>
                          <Chip
                            size="small"
                            label={routingLabel}
                            color={
                              feature.ProviderMode === "LOCAL_OLLAMA"
                                ? "warning"
                                : feature.ProviderMode === "AI_CONFIG"
                                ? "primary"
                                : feature.ProviderMode === "NONE"
                                ? "default"
                                : "default"
                            }
                            variant={feature.ProviderMode ? "outlined" : "filled"}
                          />
                        </TableCell>
                        <TableCell sx={{ minWidth: 320 }}>
                          <TextField
                            select
                            size="small"
                            fullWidth
                            value={selectionValue}
                            onChange={(e) =>
                              handleFeatureConfigChange(feature.FeatureKey, e.target.value)
                            }
                            sx={inputSx}
                            helperText={
                              inherited
                                ? "Inherited from campus. Saving creates a subcampus override."
                                : "Choose None, Local Ollama, default AI, or a configured LLM service"
                            }
                          >
                            <MenuItem value="">
                              <em>Use default AI service</em>
                            </MenuItem>
                            <MenuItem value="__NONE__">
                              None — do not use AI
                            </MenuItem>
                            <MenuItem value="__LOCAL_OLLAMA__">
                              Local Ollama (.env)
                            </MenuItem>
                            {assignmentConfigs.map((cfg) => (
                              <MenuItem key={cfg.AiConfigId} value={cfg.AiConfigId}>
                                {cfg.DisplayName || cfg.Provider} — {cfg.ModelName || "No model"}
                                {cfg.SubCampusCode ? ` (${cfg.SubCampusCode})` : " (Campus)"}
                              </MenuItem>
                            ))}
                          </TextField>
                        </TableCell>
                        <TableCell sx={{ minWidth: 220 }}>
                          {feature.AssignmentId ? (
                            <Box>
                              <Typography sx={{ fontWeight: 800, fontSize: "0.85rem" }}>
                                {feature.EffectiveDisplayName || feature.EffectiveProvider || "Configured AI"}
                              </Typography>
                              <Typography sx={{ fontSize: "0.78rem", opacity: 0.72 }}>
                                {feature.EffectiveProvider || ""}
                                {feature.EffectiveModelName ? ` / ${feature.EffectiveModelName}` : ""}
                              </Typography>
                              <Chip
                                size="small"
                                sx={{ mt: 0.5 }}
                                label={inherited ? "Inherited from campus" : feature.AssignmentScope}
                                variant="outlined"
                              />
                            </Box>
                          ) : (
                            <Typography sx={{ fontSize: "0.82rem", opacity: 0.7 }}>
                              Uses default AI service
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="contained"
                            size="small"
                            startIcon={
                              savingFeatureKey === feature.FeatureKey ? (
                                <CircularProgress size={14} color="inherit" />
                              ) : (
                                <SaveIcon />
                              )
                            }
                            sx={primaryBtnSx}
                            disabled={savingFeatureKey === feature.FeatureKey}
                            onClick={() => saveFeatureAssignment(feature)}
                          >
                            Save
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}

                  {!featureRows.length && (
                    <TableRow>
                      <TableCell colSpan={8} align="center" sx={{ py: 4, opacity: 0.7 }}>
                        No active AI features found in aiFeaturedService.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}
      </Paper>
    </>
  );

  return (
    <Box sx={pageWrapSx}>
      <Paper elevation={5} sx={rootPaperSx}>
        <Box sx={headerSx}>
          <Box>
            <Typography sx={{ fontSize: "1.1rem", fontWeight: 900, lineHeight: 1.2 }}>
              AI Provider Configurations
            </Typography>
            <Typography sx={{ opacity: 0.9, fontSize: "0.85rem" }}>
              Maintain AI services and assign them independently to UniFLOO AI features
            </Typography>
          </Box>
        </Box>

        <Box sx={{ backgroundColor: "white", borderBottom: `1px solid ${BORDER}` }}>
          <Tabs
            value={tab}
            onChange={(_, value) => setTab(value)}
            sx={{
              px: 2,
              "& .MuiTab-root": { textTransform: "none", fontWeight: 800 },
              "& .Mui-selected": { color: `${THEME_ORANGE} !important` },
              "& .MuiTabs-indicator": { backgroundColor: THEME_ORANGE },
            }}
          >
            <Tab label="AI Configs" />
            <Tab label="Feature Assignments" />
          </Tabs>
        </Box>

        <Box sx={bodySx}>
          {!!error && (
            <Alert severity="error" onClose={() => setError("")} sx={{ mb: 2, borderRadius: 2 }}>
              {error}
            </Alert>
          )}

          {!!successMessage && (
            <Snackbar
              open
              autoHideDuration={3000}
              onClose={() => setSuccessMessage("")}
            >
              <Alert severity="success" sx={{ borderRadius: 2 }}>
                {successMessage}
              </Alert>
            </Snackbar>
          )}

          {tab === 0 ? renderConfigsTab() : renderFeatureAssignmentsTab()}
        </Box>
      </Paper>
    </Box>
  );
};

export default AIProviderConfigsPage;
